import type { LucideIcon } from "lucide-react";
import {
  Calendar,
  Users,
  Scissors,
  Smartphone,
  BarChart3,
  Clock,
  UserCheck,
  Link2,
  Shield,
  Cloud,
  Headphones,
} from "lucide-react";

export const PRIMARY_CTA_LABEL = "Começar grátis";
export const PRIMARY_CTA_HREF = "/register";
export const SECONDARY_CTA_LABEL = "Conhecer o sistema";
export const SECONDARY_CTA_HREF = "#conheca";

/** Hero VSL hosted on YouTube — facade loads iframe only after Play. */
export const HERO_YOUTUBE_ID = "IBXTCvA-aT8";
export const HERO_YOUTUBE_WATCH_URL = `https://youtu.be/${HERO_YOUTUBE_ID}`;
export const HERO_VIDEO_POSTER = "/images/video02-hero-poster.jpg";

export function getHeroYoutubeEmbedUrl(autoplay: boolean): string {
  const params = new URLSearchParams({
    rel: "0",
    modestbranding: "1",
    playsinline: "1",
    autoplay: autoplay ? "1" : "0",
  });
  return `https://www.youtube-nocookie.com/embed/${HERO_YOUTUBE_ID}?${params.toString()}`;
}

export const HOME_H1 =
  "O sistema para barbearia que organiza sua agenda, clientes e equipe";

export const HOME_SECTION_IDS = {
  recursos: "recursos",
  conheca: "conheca",
  comoFunciona: "como-funciona",
  planos: "planos",
  faq: "faq",
  beneficios: "beneficios",
  clientes: "para-clientes",
} as const;

export type TrustItem = { label: string; icon: LucideIcon };

export const TRUST_STRIP: TrustItem[] = [
  { label: "Agenda online", icon: Calendar },
  { label: "Gestão de clientes", icon: Users },
  { label: "Equipe organizada", icon: UserCheck },
  { label: "Acesso pelo celular", icon: Smartphone },
];

export type FeatureItem = {
  title: string;
  description: string;
  icon: LucideIcon;
};

export const FEATURES: FeatureItem[] = [
  {
    title: "Agenda inteligente",
    description:
      "Organize os horários da equipe em uma agenda clara e evite conflitos de atendimento.",
    icon: Calendar,
  },
  {
    title: "Gestão de clientes",
    description:
      "Cadastre, busque e acompanhe o histórico de cada cliente em um só lugar.",
    icon: Users,
  },
  {
    title: "Equipe de barbeiros",
    description:
      "Defina profissionais, disponibilidade e organize a operação da barbearia por barbeiro.",
    icon: UserCheck,
  },
  {
    title: "Serviços e preços",
    description:
      "Cadastre serviços com duração e valores para padronizar atendimentos e agendamentos.",
    icon: Scissors,
  },
  {
    title: "Agendamento online",
    description:
      "Compartilhe um link público para que clientes consultem horários e agendem pelo celular.",
    icon: Link2,
  },
  {
    title: "Dashboard e relatórios",
    description:
      "Acompanhe indicadores da barbearia e tenha mais clareza sobre o dia a dia do negócio.",
    icon: BarChart3,
  },
];

export const PROBLEM_ITEMS = [
  "Agenda desorganizada e horários perdidos",
  "Dificuldade para acompanhar clientes",
  "Gestão manual e informações espalhadas",
  "Pouco tempo para focar no atendimento",
];

export const HOW_IT_WORKS = [
  {
    step: "1",
    title: "Crie sua conta",
    description:
      "Cadastre-se em poucos minutos e comece com teste grátis de 7 dias.",
  },
  {
    step: "2",
    title: "Configure sua barbearia",
    description:
      "Cadastre serviços, barbeiros, horários e personalize sua operação.",
  },
  {
    step: "3",
    title: "Organize os atendimentos",
    description:
      "Use a agenda, compartilhe o link de agendamento e centralize a gestão.",
  },
];

export const BARBER_BENEFITS = [
  "Mais organização no dia a dia",
  "Menos conflitos de horários",
  "Controle da equipe em um só painel",
  "Histórico de clientes centralizado",
  "Agendamento mais simples para recepcionar",
  "Gestão acessível de qualquer lugar",
];

export const CUSTOMER_BENEFITS = [
  "Consultar disponibilidade online",
  "Agendar serviço pelo celular",
  "Escolher barbeiro quando disponível",
  "Consultar agendamentos pelo portal público",
  "Reagendar ou cancelar conforme regras da barbearia",
];

export type PricingPlan = {
  name: string;
  price: string;
  period?: string;
  highlight?: boolean;
  badge?: string;
  features: string[];
  ctaLabel: string;
  ctaHref: string;
  accent?: string;
};

export const PRICING_PLANS: PricingPlan[] = [
  {
    name: "Teste Grátis",
    price: "Grátis",
    period: "por 7 dias",
    features: [
      "Todas funcionalidades premium",
      "Agendamentos ilimitados",
      "Dashboard completo",
    ],
    ctaLabel: "Começar teste",
    ctaHref: "/register",
  },
  {
    name: "Plano Solo",
    price: "R$ 29,90",
    period: "/mês",
    highlight: true,
    badge: "Popular",
    features: [
      "Para 1 barbeiro",
      "Agendamentos ilimitados",
      "Clientes ilimitados",
      "Relatórios completos",
    ],
    ctaLabel: "Assinar Solo",
    ctaHref: "/planos",
    accent: "orange.900",
  },
  {
    name: "Plano Duo",
    price: "R$ 49,90",
    period: "/mês",
    features: [
      "Para 2 barbeiros",
      "Seleção de barbeiro",
      "Agenda personalizada",
      "Relatórios por barbeiro",
    ],
    ctaLabel: "Assinar Duo",
    ctaHref: "/planos",
    accent: "green.500",
  },
  {
    name: "Plano Trio",
    price: "R$ 79,90",
    period: "/mês",
    features: [
      "Para 3 barbeiros",
      "Gestão completa da equipe",
      "Relatórios avançados",
      "Máximo desempenho",
    ],
    ctaLabel: "Assinar Trio",
    ctaHref: "/planos",
    accent: "blue.500",
  },
];

export type FaqItem = { question: string; answer: string };

export const HOME_FAQ: FaqItem[] = [
  {
    question: "O que é o Meu Barbeiro PRO?",
    answer:
      "É um sistema para barbearia que reúne agenda, clientes, equipe, serviços e gestão em um único painel online, acessível pelo navegador.",
  },
  {
    question: "Como funciona o sistema para barbearia?",
    answer:
      "Você cadastra sua barbearia, serviços e barbeiros, organiza a agenda e pode compartilhar um link para agendamento online com seus clientes.",
  },
  {
    question: "Posso usar pelo celular?",
    answer:
      "Sim. O Meu Barbeiro PRO funciona no navegador do celular, tablet ou computador, sem instalação.",
  },
  {
    question: "Meus clientes conseguem agendar online?",
    answer:
      "Sim. Cada barbearia pode disponibilizar uma página pública de agendamento para consulta de horários e marcação de serviços.",
  },
  {
    question: "Posso cadastrar mais de um barbeiro?",
    answer:
      "Sim. Os planos Duo e Trio permitem operar com mais barbeiros, com agenda e gestão por profissional.",
  },
  {
    question: "Preciso instalar algum programa?",
    answer:
      "Não. A plataforma é online e acessada pelo navegador, de qualquer dispositivo com internet.",
  },
  {
    question: "Existe período de teste?",
    answer:
      "Sim. Oferecemos teste grátis de 7 dias com acesso às funcionalidades premium para você conhecer o sistema.",
  },
  {
    question: "Como funciona o agendamento?",
    answer:
      "No painel você gerencia horários e atendimentos. Seus clientes podem agendar pela página pública, escolhendo serviço, data e horário disponível.",
  },
];

export type TrustProofItem = {
  title: string;
  description: string;
  icon: LucideIcon;
};

export const TRUST_PROOF: TrustProofItem[] = [
  {
    title: "Teste grátis de 7 dias",
    description: "Experimente o sistema completo antes de assinar.",
    icon: Clock,
  },
  {
    title: "100% online",
    description: "Acesse de qualquer dispositivo, sem instalar programas.",
    icon: Cloud,
  },
  {
    title: "Agendamento para clientes",
    description: "Link público para seus clientes marcarem horário online.",
    icon: Link2,
  },
  {
    title: "Suporte humanizado",
    description: "Equipe disponível para ajudar no onboarding e dúvidas.",
    icon: Headphones,
  },
];

export type SocialProofItem = {
  id: string;
  src: string;
  width: number;
  height: number;
  alt: string;
};

/** Ordem de exibição comercial — arquivos permanecem com nomes originais. */
export const SOCIAL_PROOF_ITEMS: SocialProofItem[] = [
  {
    id: "prova-02",
    src: "/images/ProvaSocial 02.png",
    width: 941,
    height: 1672,
    alt: "Feedback de barbearia sobre organização da rotina com o Meu Barbeiro PRO",
  },
  {
    id: "prova-03",
    src: "/images/ProvaSocial 03.jpeg",
    width: 732,
    height: 1586,
    alt: "Feedback de barbearia sobre organização da agenda usando o Meu Barbeiro PRO",
  },
  {
    id: "prova-01",
    src: "/images/ProvaSocial 01.jpeg",
    width: 711,
    height: 1578,
    alt: "Feedback de usuário mostrando atendimentos organizados no Meu Barbeiro PRO",
  },
];

export const SOCIAL_PROOF_COPY = {
  eyebrow: "QUEM USA, RECOMENDA",
  title: "Barbeiros reais. Resultados na rotina.",
  description:
    "Veja o que profissionais que já usam o Meu Barbeiro PRO estão dizendo sobre a organização da agenda e da rotina da barbearia.",
  authenticityLabel: "Feedbacks reais de clientes",
} as const;

export const FOOTER_GROUPS = {
  produto: [
    { label: "Recursos", href: `#${HOME_SECTION_IDS.recursos}` },
    { label: "App", href: `#${HOME_SECTION_IDS.conheca}` },
    { label: "Como funciona", href: `#${HOME_SECTION_IDS.comoFunciona}` },
    { label: "Planos", href: `#${HOME_SECTION_IDS.planos}` },
    { label: "FAQ", href: `#${HOME_SECTION_IDS.faq}` },
  ],
  suporte: [
    { label: "FAQ completo", href: "/home/faq" },
    { label: "Contato", href: "/home/contato" },
    { label: "Central de ajuda", href: "/home/suporte" },
  ],
  legal: [
    { label: "Termos de uso", href: "/home/termos" },
    { label: "Política de privacidade", href: "/home/privacidade" },
  ],
};

export const HEADER_NAV = [
  { label: "Recursos", href: `#${HOME_SECTION_IDS.recursos}` },
  { label: "App", href: `#${HOME_SECTION_IDS.conheca}` },
  { label: "Como funciona", href: `#${HOME_SECTION_IDS.comoFunciona}` },
  { label: "Planos", href: `#${HOME_SECTION_IDS.planos}` },
  { label: "FAQ", href: `#${HOME_SECTION_IDS.faq}` },
];
